import { isUnavailableToken as coreIsUnavailableToken } from '@/utils/responseGuard'

// UNAVAILABLE_TOKENS 已删除 —— token 分类统一走 responseGuard 核(union)
const ENVELOPE_KEYS = ['content', 'payload', 'body', 'extra', 'page', 'data']
const DIAGNOSTIC_ENVELOPE_KEYS = ['meta', 'trace', 'diagnostics', 'diagnostic', 'error', 'errors', 'detail', 'details']
const LIST_KEYS = ['list', 'records', 'dataList', 'items', 'pageList', 'orderRecordList', 'capitalDetailsList']
const RESPONSE_KEYS = [...ENVELOPE_KEYS, ...LIST_KEYS]
const RESPONSE_SCAN_KEYS = [...RESPONSE_KEYS, ...DIAGNOSTIC_ENVELOPE_KEYS]
const MAX_ENVELOPE_DEPTH = 6
const PAYLOAD_HINT_KEYS = [
  'account',
  'realName',
  'headUrl',
  'phone',
  'phoneNo',
  'privacyPasswdSetted',
  'bankCardList',
  'cardList',
  'bankCardCount',
  'hasBankCard',
  'list',
  'records',
  'dataList',
  'items',
  'pageList',
  'orderRecordList',
  'capitalDetailsList',
  'balance',
  'orderNo',
  'order_no',
  'orderStatus',
  'order_status'
]
const METADATA_KEYS = [
  'status',
  'state',
  'result',
  'code',
  'errorCode',
  'success',
  'msg',
  'message',
  'errorMsg',
  'errorMessage',
  'reason',
  'desc',
  'description',
  'orderStatusName'
]
const WRITE_PROOF_KEYS = [
  'writeProof',
  'write_proof',
  'serverProof',
  'server_proof',
  'affectedProof',
  'affected_proof',
  'affectedRowsProof',
  'affected_rows_proof'
]
const SERVER_READ_PROOF_KEYS = [
  'serverReadProof',
  'server_read_proof',
  'readProof',
  'read_proof',
  'queryProof',
  'query_proof'
]
const PLATFORM_LIST_KEYS = ['platformList', 'list', 'dataList', 'records']
const STRICT_PROOF_KEYS = [
  'statusProof',
  'status_proof',
  'serverOrderStatusProof',
  'server_order_status_proof',
  'orderProof',
  'order_proof',
  'paymentUrlProof',
  'payment_url_proof',
  'paymentProof',
  'payment_proof',
  'payProof',
  'pay_proof',
  'withdrawProof',
  'withdraw_proof',
  'writeProof',
  'write_proof',
  'serverProof',
  'server_proof',
  'affectedProof',
  'affected_proof',
  'affectedRowsProof',
  'affected_rows_proof'
]
const ORDER_STATUS_PROOF_KEYS = [
  'statusProof',
  'status_proof',
  'serverOrderStatusProof',
  'server_order_status_proof',
  'orderProof',
  'order_proof',
  'paymentProof',
  'payment_proof',
  'payProof',
  'pay_proof',
  'withdrawProof',
  'withdraw_proof'
]
const WRITE_COUNT_PROOF_KEYS = ['affected', 'affectedRows', 'rowCount']
const FALSEY_PROOF_TOKENS = ['0', 'false', 'null', 'undefined', 'none', 'n/a', 'na', 'mock', 'placeholder']
const LOGOUT_BLOCKED_KEY_TOKENS = [
  'readonly',
  'readonlyquery',
  'readonlymode',
  'readonlyaction',
  'readblocked',
  'backendblocked',
  'writeblocked',
  'blocked',
  'mock',
  'stub',
  'fallback',
  'fallbackused',
  'placeholder',
  'unimplemented',
  'notimplemented',
  'notsupported',
  'dataunavailable',
  'routeunavailable',
  'technicalerror',
  'technicalexception',
  'queryonly',
  'onlyquery',
  'falseproof'
]
const LOGOUT_FALSE_PROOF_KEYS = ['writeProof', 'write_proof', 'affectedRowsProof', 'affected_rows_proof']

function markerToken(value) {
  return String(value == null ? '' : value).trim().toLowerCase()
}

function compactMarkerToken(value) {
  return markerToken(value).replace(/[\s_-]+/g, '')
}

function isLogoutBlockedKey(key) {
  return LOGOUT_BLOCKED_KEY_TOKENS.includes(compactMarkerToken(key))
}

function isLogoutBlockedText(value) {
  const token = markerToken(value)
  if (!token) return false
  const compact = compactMarkerToken(value)
  const normalized = token.replace(/[_-]+/g, ' ')
  return LOGOUT_BLOCKED_KEY_TOKENS.some(blocked => compact.includes(blocked)) ||
    compact.includes('readunavailable') ||
    compact.includes('notconnected') ||
    compact.includes('unsupported') ||
    compact.includes('failclosed') ||
    compact.includes('falseproof') ||
    compact.includes('mockmode') ||
    compact.includes('mockfallback') ||
    compact.includes('placeholdermode') ||
    compact.includes('placeholderresponse') ||
    ((/(^|\s)blocked($|\s)/).test(normalized) && !normalized.includes('unblocked') && !normalized.includes('not blocked')) ||
    token.includes('只读') ||
    token.includes('只讀') ||
    token.includes('未接入') ||
    token.includes('阻止假') ||
    token.includes('只支持查询') ||
    token.includes('仅支持查询') ||
    token.includes('只支持查詢') ||
    token.includes('僅支持查詢')
}

function isActiveLogoutBlockedValue(value) {
  if (value === false || value === 0 || value === null || value === undefined || value === '') return false
  const token = markerToken(value)
  if (!token || ['false', '0', 'no', 'off'].includes(token)) return false
  return true
}

function isFalseLogoutProofValue(value) {
  const token = markerToken(value)
  return value === false || value === 0 || ['false', '0', 'no', 'off', 'placeholder'].includes(token)
}

function hasLogoutBlockedMarker(payload, depth = 0, seen = []) {
  if (depth > MAX_ENVELOPE_DEPTH || payload === null || payload === undefined) return false
  if (typeof payload !== 'object') return isLogoutBlockedText(payload)
  if (seen.includes(payload)) return false
  const nextSeen = [...seen, payload]
  if (Array.isArray(payload)) return payload.some(item => hasLogoutBlockedMarker(item, depth + 1, nextSeen))
  return Object.keys(payload).some(key => {
    const value = payload[key]
    if (isLogoutBlockedKey(key) && isActiveLogoutBlockedValue(value)) return true
    if (LOGOUT_FALSE_PROOF_KEYS.includes(key) && isFalseLogoutProofValue(value)) return true
    if (isLogoutBlockedText(value)) return true
    return value && typeof value === 'object' && hasLogoutBlockedMarker(value, depth + 1, nextSeen)
  })
}

function isGenericFailureMessage(value) {
  const token = markerToken(value)
  return !token ||
    isMojibakeMessage(value) ||
    token === 'network error' ||
    token.includes('network error') ||
    token === 'networkerror' ||
    token === 'error' ||
    token === 'request failed' ||
    token.startsWith('request failed ') ||
    token === 'requestfailed' ||
    token.startsWith('timeout ') ||
    token.includes(' timeout') ||
    token.includes('timeout of ') ||
    token.includes('exceeded') ||
    token === 'fallback'
}

function isMojibakeMessage(value) {
  const message = String(value == null ? '' : value).trim()
  if (!message) return false
  if (message.includes('�')) return true
  const compact = message.replace(/\s+/g, '')
  const markers = [
    '锛',
    '銆',
    '褰',
    '璐',
    '︽',
    '埛',
    '枡',
    '璇',
    '诲',
    '彇',
    '澶',
    '辫',
    '触',
    '绋',
    '嶅',
    '悗',
    '閲',
    '瘯',
    '鏈',
    '鍚',
    '佸',
    '妯',
    '鐢',
    '鏃',
    '堕',
    '鍦',
    '涓',
    '旀',
    '嫨',
    '湪',
    '鏄',
    '鐨'
  ]
  const markerHits = markers.reduce((count, marker) => count + (compact.includes(marker) ? 1 : 0), 0)
  if (markerHits >= 3) return true
  const latinMojibakeHits = (message.match(/[ÃÂ]|â€|å|æ|è|ç/g) || []).length
  return latinMojibakeHits >= 3
}

function failureFallbackMessage(fallback, message) {
  return isGenericFailureMessage(fallback) ? message : fallback || message
}

function hasFailureWord(value) {
  const token = markerToken(value)
  return !!token && (
    ['fail', 'failed', 'failure', 'error'].includes(token) ||
    /(?:^|[\s_-])(?:fail|failed|failure|error)(?:$|[\s_-])/.test(token) ||
    /\b(?:pay|payment|withdraw|order|request|operation|backend|route|api|server|status)[\s_-]+(?:failed|fail|failure|error)\b/.test(token) ||
    /\b(?:failed|fail|failure|error)[\s_-]+(?:pay|payment|withdraw|order|request|operation|backend|route|api|server|status)\b/.test(token)
  )
}

function isTerminalFailureToken(value) {
  const token = markerToken(value)
  return ['2', 'cancel', 'cancelled', 'canceled', 'reject', 'rejected', 'expired', 'timeout'].includes(token) ||
    hasFailureWord(value) ||
    token.includes('reject') ||
    token.includes('cancel') ||
    token.includes('expired') ||
    token.includes('timeout')
}

function isExpiredOrTimeoutToken(value) {
  const token = markerToken(value)
  return token === 'expired' ||
    token === 'timeout' ||
    token.includes('expired') ||
    token.includes('timeout')
}

function isPaymentStatusUnconfirmedToken(value) {
  const token = markerToken(value)
  return !!token && (
    token.includes('payment_status_unconfirmed') ||
    token.includes('paymentstatusunconfirmed') ||
    token.includes('payment status unconfirmed') ||
    token.includes('payment-status-unconfirmed')
  )
}

function isUnavailableToken(value) {
  return coreIsUnavailableToken(value)
}

function isHardUnavailableText(value) {
  const token = markerToken(value)
  return isUnavailableToken(value) && (
    token.includes('route_unavailable') ||
    token.includes('route-unavailable') ||
    token.includes('route unavailable') ||
    token.includes('routeunavailable') ||
    token.includes('technical_error') ||
    token.includes('technical-error') ||
    token.includes('technical error') ||
    token.includes('technicalerror') ||
    token.includes('technical_exception') ||
    token.includes('technical-exception') ||
    token.includes('technical exception') ||
    token.includes('technicalexception') ||
    token.includes('技术错误') ||
    token.includes('技术异常')
  )
}

function isReadDiagnosticText(value) {
  const token = markerToken(value)
  return !!token && (
    token.includes('read_unavailable') ||
    token.includes('read-unavailable') ||
    token.includes('read unavailable') ||
    token.includes('readunavailable') ||
    token.includes('read_blocked') ||
    token.includes('read-blocked') ||
    token.includes('read blocked') ||
    token.includes('readblocked') ||
    token.includes('read_failure') ||
    token.includes('read-failure') ||
    token.includes('read failure') ||
    token.includes('read_failed') ||
    token.includes('read-failed') ||
    token.includes('read failed')
  )
}

function isProofFailureText(value) {
  const token = markerToken(value)
  if (!token) return false
  if (
    token.includes('false_proof') ||
    token.includes('false-proof') ||
    token.includes('false proof') ||
    token.includes('falseproof')
  ) return true
  const compactToken = token.replace(/[\s_-]+/g, '')
  const mentionsProof = [
    'writeproof',
    'serverproof',
    'affectedproof',
    'affectedrowsproof',
    'statusproof',
    'orderproof',
    'paymentproof',
    'payproof',
    'withdrawproof'
  ].some(proofKey => compactToken.includes(proofKey))
  if (!mentionsProof) return false
  return token.includes('false') ||
    token.includes('missing') ||
    token.includes('invalid') ||
    token.includes('fail') ||
    token.includes('reject') ||
    token.includes('placeholder') ||
    token.includes('mock') ||
    token.includes('unavailable') ||
    token.includes('blocked') ||
    token.includes('缺少') ||
    token.includes('失败') ||
    token.includes('未确认')
}

function isConnectionDiagnosticText(value) {
  const token = markerToken(value)
  return !!token && (
    token.includes('not_connected') ||
    token.includes('not-connected') ||
    token.includes('not connected') ||
    token.includes('notconnected') ||
    token.includes('not_supported') ||
    token.includes('not-supported') ||
    token.includes('not supported') ||
    token.includes('unsupported') ||
    token.includes('data_unavailable') ||
    token.includes('data-unavailable') ||
    token.includes('data unavailable')
  )
}

function isBlockingDiagnosticText(value) {
  const token = markerToken(value)
  return isHardUnavailableText(value) ||
    isBlockedToken(value) ||
    isPaymentStatusUnconfirmedToken(value) ||
    isReadDiagnosticText(value) ||
    isProofFailureText(value) ||
    isConnectionDiagnosticText(value) ||
    token.includes('write_blocked') ||
    token.includes('write-blocked') ||
    token.includes('write blocked') ||
    token.includes('writeblocked')
}

function isVisibleTechnicalFailureMessage(value) {
  const token = markerToken(value)
  return (token.includes('技术错误') || token.includes('技术异常')) && !hasRawTechnicalDetail(value)
}

function hasRawTechnicalDetail(value) {
  const message = String(value == null ? '' : value).trim()
  const token = markerToken(message)
  return token.includes('upstream') ||
    token.includes('route failed') ||
    token.includes('request failed') ||
    token.includes('network error') ||
    token.includes('timeout') ||
    token.includes('status code') ||
    token.includes('axios') ||
    /\beconn[a-z_]*\b/i.test(message) ||
    /\b(typeerror|referenceerror|syntaxerror|rangeerror|urierror)\b/i.test(message) ||
    /\b(stack trace|traceid|trace id)\b/i.test(message) ||
    /\bhttps?:\/\/\S+/i.test(message)
}

function isBlockedToken(value) {
  const token = markerToken(value)
  if (!token || token.includes('unblocked') || token.includes('not blocked')) return false
  return token === 'block' ||
    token === 'blocked' ||
    token === 'backendblocked' ||
    token === 'paymentbackendblocked' ||
    token.includes('backend_blocked') ||
    token.includes('backendblocked') ||
    token.includes('backend blocked') ||
    token.includes('backend-blocked') ||
    token.includes('payment_backend_blocked') ||
    token.includes('paymentbackendblocked') ||
    token.includes('payment backend blocked') ||
    token.includes('payment-backend-blocked') ||
    /(^|[\s_-])blocked($|[\s_-])/.test(token)
}

function isActiveGuardFlag(value) {
  const token = markerToken(value)
  return value === true || isUnavailableToken(value) || ['true', '1', 'yes', 'on'].includes(token)
}

function isFalseProofFlag(value) {
  const token = markerToken(value)
  return value === false || value === 0 || ['false', '0', 'no', 'off'].includes(token)
}

function isInvalidStrictProofValue(value) {
  return value !== undefined && value !== null && !hasMeaningfulValue(value)
}

function hasInvalidStrictProofMarker(source) {
  if (!source || typeof source !== 'object') return false
  return STRICT_PROOF_KEYS.some(key => isInvalidStrictProofValue(source[key]))
}

export function isNeedLogin(res) {
  if (!res) return false
  const content = contentOf(res)
  const bodies = responseBodies(res)
  const flags = [
    res.needLogin,
    res.need_login,
    content.needLogin,
    content.need_login,
    ...bodies.flatMap(body => [
      body.needLogin,
      body.need_login
    ])
  ]
  if (flags.some(isNeedLoginFlag)) return true
  return [
    res.status,
    res.state,
    res.code,
    res.errorCode,
    res.msg,
    res.message,
    res.errorMsg,
    res.errorMessage,
    res.reason,
    res.desc,
    res.description,
    content.status,
    content.state,
    content.code,
    content.errorCode,
    content.msg,
    content.message,
    content.errorMsg,
    content.errorMessage,
    content.reason,
    content.desc,
    content.description,
    ...bodies.flatMap(body => [
      body.status,
      body.state,
      body.code,
      body.errorCode,
      body.msg,
      body.message,
      body.errorMsg,
      body.errorMessage,
      body.reason,
      body.desc,
      body.description
    ])
  ].some(isNeedLoginToken)
}

function isNeedLoginToken(value) {
  const token = markerToken(value)
  return token === 'need_login' ||
    token.includes('need_login') ||
    token === 'needlogin' ||
    token.includes('needlogin') ||
    token === 'login_required' ||
    token.includes('login_required') ||
    token === '401' ||
    /(^|[^\d])401([^\d]|$)/.test(token) ||
    token === 'unauthorized' ||
    token.includes('unauthorized') ||
    token === 'token_expired' ||
    token.includes('token_expired') ||
    token === 'expired_token' ||
    token.includes('expired_token') ||
    token === 'login_expired' ||
    token.includes('login_expired') ||
    token === 'session_expired' ||
    token.includes('session_expired')
}

function isNeedLoginFlag(value) {
  const token = markerToken(value)
  return value === true ||
    value === 1 ||
    ['true', '1', 'yes', 'on'].includes(token) ||
    isNeedLoginToken(value)
}

export function isOk(res) {
  if (!res || hasUnavailableMarker(res) || hasBusinessFailureMarker(res)) return false
  const content = contentOf(res)
  const bodies = responseBodies(res)
  const okStatuses = [
    res.status,
    res.state,
    res.result,
    content.status,
    content.state,
    content.result,
    ...bodies.flatMap(body => [body.status, body.state, body.result])
  ]
  const okCodes = [
    res.code,
    res.errorCode,
    content.code,
    content.errorCode,
    ...bodies.flatMap(body => [body.code, body.errorCode])
  ]
  const okSuccess = [
    res.success,
    content.success,
    ...bodies.map(body => body.success)
  ]
  return okStatuses.some(isOkStatusToken) ||
    okCodes.some(isOkCodeToken) ||
    okSuccess.some(value => value === true || markerToken(value) === 'true')
}

export function isReadOnlyQuerySuccess(res) {
  if (!res || typeof res !== 'object') return false
  if (!hasReadOnlyQueryMarker(res) || hasHardUnavailableMarker(res) || hasBusinessFailureMarker(res) || isNeedLogin(res)) return false
  const content = contentOf(res)
  const bodies = responseBodies(res)
  return [
    res.status,
    res.state,
    res.result,
    content.status,
    content.state,
    content.result,
    ...bodies.flatMap(body => [body.status, body.state, body.result])
  ].some(isOkStatusToken) || [
    res.code,
    res.errorCode,
    content.code,
    content.errorCode,
    ...bodies.flatMap(body => [body.code, body.errorCode])
  ].some(isOkCodeToken) || [
    res.success,
    content.success,
    ...bodies.map(body => body.success)
  ].some(value => value === true || markerToken(value) === 'true')
}

export function isSafeWriteSuccess(res) {
  return isOk(res) && hasWriteSuccessProof(res)
}

export function isConfirmedLogout(res) {
  if (hasLogoutBlockedMarker(res)) return false
  return isNeedLogin(res) || isSafeWriteSuccess(res)
}

export function contentOf(res) {
  if (!res || typeof res !== 'object') return {}
  return unwrapEnvelopeBody(firstEnvelopeBody(res))
}

function responseBodies(res) {
  if (!res || typeof res !== 'object') return []
  return collectEnvelopeBodies(res)
}

function normalizeEnvelopeBody(value) {
  if (Array.isArray(value)) return { list: value, dataList: value }
  return value && typeof value === 'object' ? value : {}
}

function firstEnvelopeBody(source) {
  if (Array.isArray(source)) return source
  if (!source || typeof source !== 'object') return null
  const candidates = []
  let firstEmptyEnvelope = null
  for (const key of RESPONSE_KEYS) {
    const value = source[key]
    if (Array.isArray(value)) {
      candidates.push(value)
      continue
    }
    if (value && typeof value === 'object') {
      if (Object.keys(value).length) candidates.push(value)
      if (!firstEmptyEnvelope) firstEmptyEnvelope = value
    }
  }
  const populated = candidates.find(value => hasPayloadFields(unwrapEnvelopeBody(value)))
  if (populated) return populated
  const nonEmptyEnvelope = candidates.find(value => Object.keys(normalizeEnvelopeBody(value)).length)
  if (nonEmptyEnvelope) return nonEmptyEnvelope
  if (firstEmptyEnvelope) return firstEmptyEnvelope
  return source
}

function unwrapEnvelopeBody(value, depth = 0) {
  const body = normalizeEnvelopeBody(value)
  if (!Object.keys(body).length || depth >= MAX_ENVELOPE_DEPTH) return body
  const nestedValues = RESPONSE_KEYS
    .map(key => body[key])
    .filter(nested => nested && (typeof nested === 'object' || Array.isArray(nested)))
  const populated = nestedValues.find(nested => hasPayloadFields(unwrapEnvelopeBody(nested, depth + 1)))
  const orderedNestedValues = populated
    ? [populated, ...nestedValues.filter(nested => nested !== populated)]
    : nestedValues
  for (const nested of orderedNestedValues) {
    const unwrapped = unwrapEnvelopeBody(nested, depth + 1)
    if (!Object.keys(unwrapped).length) continue
    const parent = { ...body }
    RESPONSE_KEYS.forEach(envelopeKey => {
      delete parent[envelopeKey]
    })
    return {
      ...parent,
      ...unwrapped
    }
  }
  return body
}

function hasPayloadFields(source) {
  if (!source || typeof source !== 'object') return false
  return Object.keys(source).some(key => (
    PAYLOAD_HINT_KEYS.includes(key) ||
    !METADATA_KEYS.includes(key)
  ))
}

function hasBusinessPayloadFields(source) {
  if (!source || typeof source !== 'object') return false
  const body = normalizeEnvelopeBody(source)
  return Object.keys(body).some(key => (
    PAYLOAD_HINT_KEYS.includes(key) ||
    (!METADATA_KEYS.includes(key) && !RESPONSE_KEYS.includes(key))
  ))
}

function unavailableTextSources(payload, content, bodies) {
  const sources = [payload, content, ...bodies].filter(source => source && typeof source === 'object')
  if (!sources.some(hasBusinessPayloadFields)) return sources
  return sources.filter(hasBusinessPayloadFields)
}

function collectEnvelopeBodies(source, depth = 0, out = []) {
  if (!source || typeof source !== 'object' || depth >= MAX_ENVELOPE_DEPTH) return out
  if (Array.isArray(source)) {
    source.forEach(item => {
      if (item && typeof item === 'object') {
        out.push(item)
        collectEnvelopeBodies(item, depth + 1, out)
      }
    })
    return out
  }
  RESPONSE_SCAN_KEYS.forEach(key => {
    const value = source[key]
    if (!value || (typeof value !== 'object' && !Array.isArray(value))) return
    const body = normalizeEnvelopeBody(value)
    if (Object.keys(body).length) out.push(body)
    collectEnvelopeBodies(value, depth + 1, out)
  })
  return out
}

function hasWriteSuccessProof(res) {
  const content = contentOf(res)
  const bodies = responseBodies(res)
  return [res, content, ...bodies].some(source => {
    if (!source || typeof source !== 'object') return false
    return WRITE_PROOF_KEYS.some(key => hasMeaningfulWriteProof(key, source[key])) ||
      WRITE_COUNT_PROOF_KEYS.some(key => hasPositiveCount(source[key]))
  })
}

function hasMeaningfulWriteProof(key, value) {
  if (key === 'headUrl') {
    if (value === 0 || value === '0') return true
  }
  if (key === 'accessToken') {
    return typeof value === 'string' && hasMeaningfulValue(value)
  }
  if (STRICT_PROOF_KEYS.includes(key)) {
    return value !== true && markerToken(value) !== 'true' && hasMeaningfulValue(value)
  }
  return hasMeaningfulValue(value)
}

function hasMeaningfulValue(value) {
  if (Array.isArray(value)) {
    return value.length > 0 && !value.some(item => hasProofUnavailableMarker(item))
  }
  if (value && typeof value === 'object') {
    return Object.keys(value).length > 0 && !hasUnavailableMarker(value)
  }
  if (typeof value === 'boolean') return value
  const valueText = text(value).trim()
  const token = markerToken(valueText)
  return valueText !== '' &&
    !FALSEY_PROOF_TOKENS.includes(token) &&
    !isUnavailableToken(valueText) &&
    !hasFailureWord(valueText)
}

function hasProofUnavailableMarker(value) {
  if (!value || typeof value !== 'object') {
    return isUnavailableToken(value) || hasFailureWord(value)
  }
  return hasUnavailableMarker(value)
}

function hasPositiveCount(value) {
  const token = markerToken(value)
  if (!/^\d+$/.test(token)) return false
  return Number(token) > 0
}

function isPositiveProofValue(value) {
  if (Array.isArray(value)) return false
  if (value && typeof value === 'object') return false
  if (typeof value === 'boolean') return value === true
  const valueText = text(value).trim()
  const token = markerToken(valueText)
  return valueText !== '' &&
    !FALSEY_PROOF_TOKENS.includes(token) &&
    !isUnavailableToken(valueText) &&
    !hasFailureWord(valueText)
}

export function hasPositivePaymentStatusProof(item = {}) {
  const content = contentOf(item)
  const bodies = responseBodies(item)
  return [item, content, ...bodies].some(source => {
    if (!source || typeof source !== 'object') return false
    return ORDER_STATUS_PROOF_KEYS.some(key => isPositiveProofValue(source[key])) ||
      WRITE_COUNT_PROOF_KEYS.some(key => hasPositiveCount(source[key]))
  })
}

function isOkStatusToken(value) {
  const token = markerToken(value)
  return ['ok', 'success', 'successful', 'succeed', 'succeeded', '0', '00', '0000', '000000', '200'].includes(token)
}

function isOkCodeToken(value) {
  const token = markerToken(value)
  return ['0', '00', '0000', '000000', '200', 'ok', 'success'].includes(token)
}

function hasBusinessFailureMarker(res) {
  const content = contentOf(res)
  const bodies = responseBodies(res)
  return [res, content, ...bodies].some(source => {
    if (!source || typeof source !== 'object') return false
    if (isFailureFlag(source.failed) || isFailureFlag(source.failure)) return true
    return [
      source.status,
      source.state,
      source.result
    ].some(isFailureStatusToken) || [
      source.code,
      source.errorCode
    ].some(isFailureCodeToken)
  })
}

function isFailureStatusToken(value) {
  const token = markerToken(value)
  return ['fail', 'failed', 'failure', 'error', 'rejected', 'reject', 'disabled'].includes(token)
}

function isFailureFlag(value) {
  const token = markerToken(value)
  return value === true || ['true', '1', 'yes', 'on'].includes(token) || isFailureStatusToken(value)
}

function isFailureCodeToken(value) {
  const token = markerToken(value)
  if (!token || isOkCodeToken(token) || token === 'need_login') return false
  if (/^[45]\d{2,}$/.test(token)) return true
  return hasFailureWord(token) || token.startsWith('e_')
}

function isOptimisticSuccessMessage(value) {
  const token = markerToken(value)
  if (!token) return false
  if (['ok', 'success', 'successful', 'succeed', 'succeeded', 'done', 'saved', 'complete', 'completed', 'sucesso', 'exito', 'éxito', '成功'].includes(token)) {
    return true
  }
  const hasSuccessCopy = /\b(?:success|successful|succeed|succeeded|done|saved|complete|completed)\b/.test(token) ||
    token.includes('sucesso') ||
    token.includes('éxito') ||
    token.includes('exito') ||
    token.includes('成功')
  const hasFailureContext = hasFailureWord(token) ||
    isUnavailableToken(token) ||
    isBlockedToken(token) ||
    token.includes('阻止') ||
    token.includes('失败') ||
    token.includes('失敗') ||
    token.includes('不可用') ||
    token.includes('未接入') ||
    token.includes('fake') ||
    token.includes('只读') ||
    token.includes('只讀')
  return hasSuccessCopy && !hasFailureContext
}

function hasReadOnlyQueryMarker(payload) {
  const content = contentOf(payload)
  const bodies = responseBodies(payload)
  return [payload, content, ...bodies].some(source => {
    if (!source || typeof source !== 'object') return false
    return [
      source.readOnly,
      source.readOnlyQuery,
      source.readOnlyAction,
      source.readonly,
      source.read_only,
      source.read_only_query,
      source.read_only_action,
      source.queryOnly,
      source.query_only,
      source.onlyQuery,
      source.only_query,
      source.status,
      source.state,
      source.result,
      source.message,
      source.msg,
      source.reason,
      source.desc,
      source.description
    ].some(isReadOnlyQueryToken)
  })
}

function isReadOnlyQueryToken(value) {
  const token = markerToken(value)
  if (!token) return false
  return value === true ||
    value === 1 ||
    ['true', '1', 'yes', 'on'].includes(token) ||
    token.includes('read_only') ||
    token.includes('read-only') ||
    token.includes('read only') ||
    token.includes('readonly') ||
    token.includes('query_only') ||
    token.includes('query-only') ||
    token.includes('query only') ||
    token.includes('only_query') ||
    token.includes('only-query') ||
    token.includes('only query') ||
    token.includes('只支持查询') ||
    token.includes('仅支持查询') ||
    token.includes('只支持查詢') ||
    token.includes('僅支持查詢') ||
    token.includes('只读') ||
    token.includes('只讀')
}

function hasHardUnavailableMarker(payload) {
  const content = contentOf(payload)
  const bodies = responseBodies(payload)
  return [payload, content, ...bodies].some(source => {
    if (!source || typeof source !== 'object') return false
    if ([
      ...STRICT_PROOF_KEYS.map(key => source[key]),
      source.success,
      source.receiveSuccess,
      source.claimSuccess,
      source.signSuccess,
      source.paySuccess
    ].some(isFalseProofFlag)) return true
    if ([
      source.mock,
      source.mockMode,
      source.mock_mode,
      source.mockFallback,
      source.mock_fallback,
      source.unimplemented,
      source.notImplemented,
      source.not_implemented,
      source.notSupported,
      source.not_supported,
      source.notConnected,
      source.not_connected,
      source.unsupported,
      source.unavailable,
      source.dataUnavailable,
      source.data_unavailable,
      source.readUnavailable,
      source.read_unavailable,
      source.routeUnavailable,
      source.route_unavailable,
      source.technicalError,
      source.technical_error,
      source.technicalException,
      source.technical_exception,
      source.technicalMarker,
      source.technical_marker,
      source.backendFailure,
      source.backend_failure,
      source.readBlocked,
      source.read_blocked,
      source.readFailure,
      source.read_failure,
      source.readFailed,
      source.read_failed,
      source.fallback,
      source.fallbackUsed,
      source.fallback_used,
      source.failClosed,
      source.fail_closed,
      source.stub,
      source.placeholder,
      source.placeholderMode,
      source.placeholder_mode,
      source.placeholderResponse,
      source.placeholder_response,
      source.writeBlocked,
      source.write_blocked,
      source.readOnlyMode,
      source.read_only_mode,
      source.blocked,
      source.backendBlocked,
      source.backend_blocked,
      source.paymentBackendBlocked,
      source.payment_backend_blocked,
      source.paymentStatusUnconfirmed,
      source.payment_status_unconfirmed,
      source.needLogin,
      source.need_login
    ].some(isActiveGuardFlag)) return true
    return [
      source.status,
      source.state,
      source.result,
      source.code,
      source.errorCode,
      source.msg,
      source.message,
      source.errorMsg,
      source.errorMessage,
      source.reason,
      source.desc,
      source.description
    ].some(value => isUnavailableToken(value) && !isReadOnlyQueryToken(value))
  })
}

export function failMessage(res, fallback) {
  if (isNeedLogin(res)) return 'Please log in to continue'
  if (hasUnavailableMarker(res)) {
    const message = firstMessage(res)
    if (message && !isOptimisticSuccessMessage(message) && !isGenericFailureMessage(message) && (isVisibleTechnicalFailureMessage(message) || !isHardUnavailableText(message))) return message
    return unavailableMessage(res, fallback)
  }
  const message = firstMessage(res)
  if (message && !isGenericFailureMessage(message)) return message
  return failureFallbackMessage(fallback, '当前账户功能暂时不可用，请稍后重试。')
}

export function ensureLogin(vm, message = 'Please log in to continue') {
  if (readLocalToken()) return true
  promptLogin(vm, message)
  return false
}

export function handleNeedLogin(vm, res, message = 'Please log in to continue', query = {}, options = { replace: true }) {
  if (!isNeedLogin(res)) return false
  promptLogin(vm, failMessage(res, message), query, options)
  return true
}

function readLocalToken() {
  return text(safeGetStorageItem(getLocalStorage(), 'token'))
}

function clearLocalToken() {
  return safeRemoveStorageItem(getLocalStorage(), 'token')
}

function browserGlobal() {
  try {
    if (typeof window !== 'undefined') return window
  } catch (e) {
    return null
  }
  return null
}

function getLocalStorage() {
  try {
    const root = browserGlobal()
    const storage = root && root.localStorage
    return storage && (typeof storage.getItem === 'function' || typeof storage.removeItem === 'function') ? storage : null
  } catch (e) {
    return null
  }
}

function safeGetStorageItem(storage, key) {
  try {
    return storage && typeof storage.getItem === 'function' ? storage.getItem(key) : ''
  } catch (e) {
    return ''
  }
}

function safeRemoveStorageItem(storage, key) {
  try {
    if (!storage || typeof storage.removeItem !== 'function') return false
    storage.removeItem(key)
    return true
  } catch (e) {
    return false
  }
}

function promptLogin(vm, message, query = {}, options = { replace: true }) {
  clearLocalToken()
  if (vm && typeof vm.$toast === 'function') {
    vm.$toast({ message: message || 'Please log in to continue', icon: 'info' })
  }
  if (vm && vm.$bus && typeof vm.$bus.$emit === 'function') {
    vm.$bus.$emit('openLogin')
  }
  if (vm && typeof vm.$jumpTo === 'function') {
    vm.$jumpTo('/home', query || {}, options || { replace: true })
  }
}

function firstMessage(res) {
  const content = contentOf(res)
  const bodies = responseBodies(res)
  return firstValueFromSources([res, content, ...bodies], [
    'msg',
    'message',
    'errorMsg',
    'errorMessage',
    'reason',
    'desc',
    'description',
    'orderStatusName'
  ])
}

function firstValueFromSources(sources, keys) {
  for (const source of sources) {
    const value = firstValue(source, keys)
    if (text(value)) return value
  }
  return ''
}

function hasPositiveServerReadProof(source) {
  if (!source || typeof source !== 'object') return false
  return SERVER_READ_PROOF_KEYS.some(key => isPositiveProofValue(source[key]))
}

function hasExplicitEmptyPlatformArray(source) {
  if (!source || typeof source !== 'object') return false
  return PLATFORM_LIST_KEYS.some(key => Array.isArray(source[key]) && source[key].length === 0)
}

function hasPositiveTotal(source) {
  if (!source || typeof source !== 'object') return false
  return ['total', 'totalCount', 'total_count', 'count'].some(key => {
    if (source[key] === undefined || source[key] === null || source[key] === '') return false
    const value = Number(String(source[key]).replace(/,/g, ''))
    return Number.isFinite(value) && value > 0
  })
}

function hasProvenEmptyPlatformHardBlocker(sources) {
  if (sources.some(hasInvalidStrictProofMarker)) return true
  const hardFlags = sources.flatMap(source => {
    if (!source || typeof source !== 'object') return []
    return [
      source.readOnly,
      source.readOnlyAction,
      source.readOnlyMode,
      source.readonly,
      source.read_only,
      source.read_only_action,
      source.read_only_mode,
      source.mock,
      source.mockMode,
      source.mock_mode,
      source.mockFallback,
      source.mock_fallback,
      source.unimplemented,
      source.notImplemented,
      source.not_implemented,
      source.notSupported,
      source.not_supported,
      source.notConnected,
      source.not_connected,
      source.unsupported,
      source.unavailable,
      source.dataUnavailable,
      source.data_unavailable,
      source.readUnavailable,
      source.read_unavailable,
      source.routeUnavailable,
      source.route_unavailable,
      source.technicalError,
      source.technical_error,
      source.technicalException,
      source.technical_exception,
      source.technicalMarker,
      source.technical_marker,
      source.backendFailure,
      source.backend_failure,
      source.readBlocked,
      source.read_blocked,
      source.readFailure,
      source.read_failure,
      source.readFailed,
      source.read_failed,
      source.fallback,
      source.fallbackUsed,
      source.fallback_used,
      source.failClosed,
      source.fail_closed,
      source.placeholder,
      source.placeholderMode,
      source.placeholder_mode,
      source.placeholderResponse,
      source.placeholder_response,
      source.queryOnly,
      source.query_only,
      source.onlyQuery,
      source.only_query,
      source.stub,
      source.writeBlocked,
      source.write_blocked,
      source.blocked,
      source.backendBlocked,
      source.backend_blocked,
      source.paymentBackendBlocked,
      source.payment_backend_blocked,
      source.paymentStatusUnconfirmed,
      source.payment_status_unconfirmed,
      source.falseProof,
      source.false_proof,
      source.needLogin,
      source.need_login
    ]
  })
  if (hardFlags.some(isActiveGuardFlag)) return true
  if (sources.some(hasPositiveTotal)) return true
  if (sources.some(source => source && typeof source === 'object' && (source.success === false || markerToken(source.success) === 'false'))) return true
  return sources.some(source => source && typeof source === 'object' && [
    source.status,
    source.state,
    source.result,
    source.code,
    source.errorCode
  ].some(isExpiredOrTimeoutToken))
}

export function hasProvenEmptyPlatformList(res) {
  if (!res || typeof res !== 'object') return false
  const content = contentOf(res)
  const bodies = responseBodies(res)
  const sources = [res, content, ...bodies].filter(source => source && typeof source === 'object')
  return sources.some(hasPositiveServerReadProof) &&
    sources.some(hasExplicitEmptyPlatformArray) &&
    !hasProvenEmptyPlatformHardBlocker(sources)
}

export function hasUnavailableMarker(payload) {
  if (!payload || typeof payload !== 'object') return false
  const content = contentOf(payload)
  const bodies = responseBodies(payload)
  const allowPlatformReadOnlyQuery = hasProvenEmptyPlatformList(payload)
  const directFlags = [
    payload.readOnly,
    allowPlatformReadOnlyQuery ? false : payload.readOnlyQuery,
    payload.readOnlyAction,
    payload.readOnlyMode,
    payload.readonly,
    payload.read_only,
    allowPlatformReadOnlyQuery ? false : payload.read_only_query,
    payload.read_only_action,
    payload.read_only_mode,
    payload.mock,
    payload.mockMode,
    payload.mock_mode,
    payload.mockFallback,
    payload.mock_fallback,
    payload.unimplemented,
    payload.notImplemented,
    payload.not_implemented,
    payload.notSupported,
    payload.not_supported,
    payload.notConnected,
    payload.not_connected,
    payload.unsupported,
    payload.unavailable,
    payload.dataUnavailable,
    payload.data_unavailable,
    payload.readUnavailable,
    payload.read_unavailable,
    payload.routeUnavailable,
    payload.route_unavailable,
    payload.technicalError,
    payload.technical_error,
    payload.technicalException,
    payload.technical_exception,
    payload.technicalMarker,
    payload.technical_marker,
    payload.backendFailure,
    payload.backend_failure,
    payload.readBlocked,
    payload.read_blocked,
    payload.readFailure,
    payload.read_failure,
    payload.readFailed,
    payload.read_failed,
    payload.fallback,
    payload.fallbackUsed,
    payload.fallback_used,
    payload.failClosed,
    payload.fail_closed,
    payload.placeholder,
    payload.placeholderMode,
    payload.placeholder_mode,
    payload.placeholderResponse,
    payload.placeholder_response,
    payload.queryOnly,
    payload.query_only,
    payload.onlyQuery,
    payload.only_query,
    payload.stub,
    payload.writeBlocked,
    payload.write_blocked,
    payload.blocked,
    payload.backendBlocked,
    payload.backend_blocked,
    payload.paymentBackendBlocked,
    payload.payment_backend_blocked,
    payload.paymentStatusUnconfirmed,
    payload.payment_status_unconfirmed,
    payload.falseProof,
    payload.false_proof,
    payload.needLogin,
    payload.need_login,
    content.readOnly,
    allowPlatformReadOnlyQuery ? false : content.readOnlyQuery,
    content.readOnlyAction,
    content.readOnlyMode,
    content.readonly,
    content.read_only,
    allowPlatformReadOnlyQuery ? false : content.read_only_query,
    content.read_only_action,
    content.read_only_mode,
    content.mock,
    content.mockMode,
    content.mock_mode,
    content.mockFallback,
    content.mock_fallback,
    content.unimplemented,
    content.notImplemented,
    content.not_implemented,
    content.notSupported,
    content.not_supported,
    content.notConnected,
    content.not_connected,
    content.unsupported,
    content.unavailable,
    content.dataUnavailable,
    content.data_unavailable,
    content.readUnavailable,
    content.read_unavailable,
    content.routeUnavailable,
    content.route_unavailable,
    content.technicalError,
    content.technical_error,
    content.technicalException,
    content.technical_exception,
    content.technicalMarker,
    content.technical_marker,
    content.backendFailure,
    content.backend_failure,
    content.readBlocked,
    content.read_blocked,
    content.readFailure,
    content.read_failure,
    content.readFailed,
    content.read_failed,
    content.fallback,
    content.fallbackUsed,
    content.fallback_used,
    content.failClosed,
    content.fail_closed,
    content.placeholder,
    content.placeholderMode,
    content.placeholder_mode,
    content.placeholderResponse,
    content.placeholder_response,
    content.queryOnly,
    content.query_only,
    content.onlyQuery,
    content.only_query,
    content.stub,
    content.writeBlocked,
    content.write_blocked,
    content.blocked,
    content.backendBlocked,
    content.backend_blocked,
    content.paymentBackendBlocked,
    content.payment_backend_blocked,
    content.paymentStatusUnconfirmed,
    content.payment_status_unconfirmed,
    content.falseProof,
    content.false_proof,
    content.needLogin,
    content.need_login,
    ...bodies.flatMap(body => [
      body.readOnly,
      allowPlatformReadOnlyQuery ? false : body.readOnlyQuery,
      body.readOnlyAction,
      body.readOnlyMode,
      body.readonly,
      body.read_only,
      allowPlatformReadOnlyQuery ? false : body.read_only_query,
      body.read_only_action,
      body.read_only_mode,
      body.mock,
      body.mockMode,
      body.mock_mode,
      body.mockFallback,
      body.mock_fallback,
      body.unimplemented,
      body.notImplemented,
      body.not_implemented,
      body.notSupported,
      body.not_supported,
      body.notConnected,
      body.not_connected,
      body.unsupported,
      body.unavailable,
      body.dataUnavailable,
      body.data_unavailable,
      body.readUnavailable,
      body.read_unavailable,
      body.routeUnavailable,
      body.route_unavailable,
      body.technicalError,
      body.technical_error,
      body.technicalException,
      body.technical_exception,
      body.technicalMarker,
      body.technical_marker,
      body.backendFailure,
      body.backend_failure,
      body.readBlocked,
      body.read_blocked,
      body.readFailure,
      body.read_failure,
      body.readFailed,
      body.read_failed,
      body.fallback,
      body.fallbackUsed,
      body.fallback_used,
      body.failClosed,
      body.fail_closed,
      body.placeholder,
      body.placeholderMode,
      body.placeholder_mode,
      body.placeholderResponse,
      body.placeholder_response,
      body.queryOnly,
      body.query_only,
      body.onlyQuery,
      body.only_query,
      body.stub,
      body.writeBlocked,
      body.write_blocked,
      body.blocked,
      body.backendBlocked,
      body.backend_blocked,
      body.paymentBackendBlocked,
      body.payment_backend_blocked,
      body.paymentStatusUnconfirmed,
      body.payment_status_unconfirmed,
      body.falseProof,
      body.false_proof,
      body.needLogin,
      body.need_login
    ])
  ]
  if ([payload, content, ...bodies].some(hasInvalidStrictProofMarker)) return true
  if (directFlags.some(isActiveGuardFlag)) return true
  if ([payload.success, content.success, ...bodies.map(body => body.success)].some(value => value === false || markerToken(value) === 'false')) return true
  if ([
    payload.status,
    payload.state,
    payload.result,
    payload.code,
    payload.errorCode,
    content.status,
    content.state,
    content.result,
    content.code,
    content.errorCode
  ].some(isExpiredOrTimeoutToken)) return true
  const markerSources = [payload, content, ...bodies]
  const textSources = unavailableTextSources(payload, content, bodies)
  const hardTextValues = markerSources.flatMap(source => {
    if (!source || typeof source !== 'object') return []
    return [source.msg, source.message, source.errorMsg, source.errorMessage, source.reason, source.desc, source.description]
  })
  if (hardTextValues.some(isBlockingDiagnosticText)) return true
  return [
    ...markerSources.flatMap(source => {
      if (!source || typeof source !== 'object') return []
      return [
        source.status,
        source.state,
        source.result,
        source.marker,
        source.type,
        source.code,
        source.errorCode,
        source.notSupported,
        source.not_supported,
        source.notConnected,
        source.not_connected,
        source.unsupported,
        source.unavailable,
        source.dataUnavailable,
        source.data_unavailable,
        source.readUnavailable,
        source.read_unavailable,
        source.routeUnavailable,
        source.route_unavailable,
        source.technicalError,
        source.technical_error,
        source.technicalException,
        source.technical_exception,
        source.technicalMarker,
        source.technical_marker,
        source.backendFailure,
        source.backend_failure,
        source.readBlocked,
        source.read_blocked,
        source.readFailure,
        source.read_failure,
        source.readFailed,
        source.read_failed,
        source.fallback,
        source.fallbackUsed,
        source.fallback_used,
        source.failClosed,
        source.fail_closed,
        source.placeholder,
        source.placeholderMode,
        source.placeholder_mode,
        source.placeholderResponse,
        source.placeholder_response,
        source.queryOnly,
        source.query_only,
        source.onlyQuery,
        source.only_query,
        source.writeBlocked,
        source.write_blocked,
        source.blocked,
        source.backendBlocked,
        source.backend_blocked,
        source.paymentBackendBlocked,
        source.payment_backend_blocked,
        source.paymentStatusUnconfirmed,
        source.payment_status_unconfirmed,
        source.success,
        source.receiveSuccess,
        source.claimSuccess,
        source.signSuccess,
        source.paySuccess,
        source.receiveStatus,
        source.claimStatus,
        source.promoStatus
      ]
    }),
    ...textSources.flatMap(source => [
      source.msg,
      source.message,
      source.errorMsg,
      source.errorMessage,
      source.reason,
      source.desc,
      source.description
    ])
  ].some(isUnavailableToken)
}

export function unavailableMessage(res, fallback) {
  const content = contentOf(res)
  const bodies = responseBodies(res)
  const values = [
    res && res.status,
    res && res.state,
    res && res.result,
    res && res.marker,
    res && res.type,
    res && res.msg,
    res && res.message,
    res && res.errorMsg,
    res && res.errorMessage,
    res && res.reason,
    res && res.desc,
    res && res.description,
    res && res.code,
    res && res.errorCode,
    res && res.notSupported,
    res && res.not_supported,
    res && res.notConnected,
    res && res.not_connected,
    res && res.unsupported,
    res && res.unavailable,
    res && res.dataUnavailable,
    res && res.data_unavailable,
    res && res.readUnavailable,
    res && res.read_unavailable,
    res && res.routeUnavailable,
    res && res.route_unavailable,
    res && res.technicalError,
    res && res.technical_error,
    res && res.technicalException,
    res && res.technical_exception,
    res && res.technicalMarker,
    res && res.technical_marker,
    res && res.backendFailure,
    res && res.backend_failure,
    res && res.readBlocked,
    res && res.read_blocked,
    res && res.readFailure,
    res && res.read_failure,
    res && res.readFailed,
    res && res.read_failed,
    res && res.fallback,
    res && res.fallbackUsed,
    res && res.fallback_used,
    res && res.failClosed,
    res && res.fail_closed,
    res && res.placeholder,
    res && res.placeholderMode,
    res && res.placeholder_mode,
    res && res.placeholderResponse,
    res && res.placeholder_response,
    res && res.queryOnly,
    res && res.query_only,
    res && res.onlyQuery,
    res && res.only_query,
    res && res.writeBlocked,
    res && res.write_blocked,
    res && res.blocked,
    res && res.backendBlocked,
    res && res.backend_blocked,
    res && res.paymentBackendBlocked,
    res && res.payment_backend_blocked,
    res && res.paymentStatusUnconfirmed,
    res && res.payment_status_unconfirmed,
    res && res.success,
    content.status,
    content.state,
    content.result,
    content.marker,
    content.type,
    content.msg,
    content.message,
    content.errorMsg,
    content.errorMessage,
    content.reason,
    content.desc,
    content.description,
    content.code,
    content.errorCode,
    content.notSupported,
    content.not_supported,
    content.notConnected,
    content.not_connected,
    content.unsupported,
    content.unavailable,
    content.dataUnavailable,
    content.data_unavailable,
    content.readUnavailable,
    content.read_unavailable,
    content.routeUnavailable,
    content.route_unavailable,
    content.technicalError,
    content.technical_error,
    content.technicalException,
    content.technical_exception,
    content.technicalMarker,
    content.technical_marker,
    content.backendFailure,
    content.backend_failure,
    content.readBlocked,
    content.read_blocked,
    content.readFailure,
    content.read_failure,
    content.readFailed,
    content.read_failed,
    content.fallback,
    content.fallbackUsed,
    content.fallback_used,
    content.failClosed,
    content.fail_closed,
    content.placeholder,
    content.placeholderMode,
    content.placeholder_mode,
    content.placeholderResponse,
    content.placeholder_response,
    content.queryOnly,
    content.query_only,
    content.onlyQuery,
    content.only_query,
    content.writeBlocked,
    content.write_blocked,
    content.blocked,
    content.backendBlocked,
    content.backend_blocked,
    content.paymentBackendBlocked,
    content.payment_backend_blocked,
    content.paymentStatusUnconfirmed,
    content.payment_status_unconfirmed,
    content.success,
    ...bodies.flatMap(body => [
      body.status,
      body.state,
      body.result,
      body.marker,
      body.type,
      body.msg,
      body.message,
      body.errorMsg,
      body.errorMessage,
      body.reason,
      body.desc,
      body.description,
      body.code,
      body.errorCode,
      body.notSupported,
      body.not_supported,
      body.notConnected,
      body.not_connected,
      body.unsupported,
      body.unavailable,
      body.dataUnavailable,
      body.data_unavailable,
      body.readUnavailable,
      body.read_unavailable,
      body.routeUnavailable,
      body.route_unavailable,
      body.technicalError,
      body.technical_error,
      body.technicalException,
      body.technical_exception,
      body.technicalMarker,
      body.technical_marker,
      body.backendFailure,
      body.backend_failure,
      body.readBlocked,
      body.read_blocked,
      body.readFailure,
      body.read_failure,
      body.readFailed,
      body.read_failed,
      body.fallback,
      body.fallbackUsed,
      body.fallback_used,
      body.failClosed,
      body.fail_closed,
      body.placeholder,
      body.placeholderMode,
      body.placeholder_mode,
      body.placeholderResponse,
      body.placeholder_response,
      body.queryOnly,
      body.query_only,
      body.onlyQuery,
      body.only_query,
      body.writeBlocked,
      body.write_blocked,
      body.blocked,
      body.backendBlocked,
      body.backend_blocked,
      body.paymentBackendBlocked,
      body.payment_backend_blocked,
      body.paymentStatusUnconfirmed,
      body.payment_status_unconfirmed,
      body.success
    ])
  ]
  const blockedFlags = [res && res.blocked, res && res.failClosed, res && res.fail_closed, res && res.backendBlocked, res && res.backend_blocked, res && res.paymentBackendBlocked, res && res.payment_backend_blocked, content.blocked, content.failClosed, content.fail_closed, content.backendBlocked, content.backend_blocked, content.paymentBackendBlocked, content.payment_backend_blocked, ...bodies.flatMap(body => [body.blocked, body.failClosed, body.fail_closed, body.backendBlocked, body.backend_blocked, body.paymentBackendBlocked, body.payment_backend_blocked])]
  const paymentUnconfirmedFlags = [res && res.paymentStatusUnconfirmed, res && res.payment_status_unconfirmed, content.paymentStatusUnconfirmed, content.payment_status_unconfirmed, ...bodies.flatMap(body => [body.paymentStatusUnconfirmed, body.payment_status_unconfirmed])]
  const writeFlags = [res && res.writeBlocked, res && res.write_blocked, content.writeBlocked, content.write_blocked, ...bodies.flatMap(body => [body.writeBlocked, body.write_blocked])]
  const readFlags = [res && res.readOnly, res && res.readOnlyQuery, res && res.readOnlyAction, res && res.readOnlyMode, res && res.readUnavailable, res && res.read_unavailable, res && res.readBlocked, res && res.read_blocked, res && res.readonly, res && res.read_only, res && res.read_only_query, res && res.read_only_action, res && res.read_only_mode, content.readOnly, content.readOnlyQuery, content.readOnlyAction, content.readOnlyMode, content.readUnavailable, content.read_unavailable, content.readBlocked, content.read_blocked, content.readonly, content.read_only, content.read_only_query, content.read_only_action, content.read_only_mode, ...bodies.flatMap(body => [body.readOnly, body.readOnlyQuery, body.readOnlyAction, body.readOnlyMode, body.readUnavailable, body.read_unavailable, body.readBlocked, body.read_blocked, body.readonly, body.read_only, body.read_only_query, body.read_only_action, body.read_only_mode])]
  const unimplementedFlags = [res && res.unimplemented, res && res.notImplemented, res && res.not_implemented, content.unimplemented, content.notImplemented, content.not_implemented, ...bodies.flatMap(body => [body.unimplemented, body.notImplemented, body.not_implemented])]
  const unsupportedFlags = [res && res.notSupported, res && res.not_supported, res && res.unsupported, content.notSupported, content.not_supported, content.unsupported, ...bodies.flatMap(body => [body.notSupported, body.not_supported, body.unsupported])]
  const notConnectedFlags = [res && res.notConnected, res && res.not_connected, res && res.routeUnavailable, res && res.route_unavailable, content.notConnected, content.not_connected, content.routeUnavailable, content.route_unavailable, ...bodies.flatMap(body => [body.notConnected, body.not_connected, body.routeUnavailable, body.route_unavailable])]
  const technicalFlags = [res && res.technicalError, res && res.technical_error, res && res.technicalException, res && res.technical_exception, res && res.technicalMarker, res && res.technical_marker, res && res.backendFailure, res && res.backend_failure, content.technicalError, content.technical_error, content.technicalException, content.technical_exception, content.technicalMarker, content.technical_marker, content.backendFailure, content.backend_failure, ...bodies.flatMap(body => [body.technicalError, body.technical_error, body.technicalException, body.technical_exception, body.technicalMarker, body.technical_marker, body.backendFailure, body.backend_failure])]
  const fallbackFlags = [res && res.unavailable, res && res.dataUnavailable, res && res.data_unavailable, res && res.fallback, res && res.fallbackUsed, res && res.fallback_used, res && res.placeholder, res && res.placeholderMode, res && res.placeholder_mode, res && res.placeholderResponse, res && res.placeholder_response, content.unavailable, content.dataUnavailable, content.data_unavailable, content.fallback, content.fallbackUsed, content.fallback_used, content.placeholder, content.placeholderMode, content.placeholder_mode, content.placeholderResponse, content.placeholder_response, ...bodies.flatMap(body => [body.unavailable, body.dataUnavailable, body.data_unavailable, body.fallback, body.fallbackUsed, body.fallback_used, body.placeholder, body.placeholderMode, body.placeholder_mode, body.placeholderResponse, body.placeholder_response])]
  const readFailureFlags = [res && res.readFailure, res && res.read_failure, res && res.readFailed, res && res.read_failed, content.readFailure, content.read_failure, content.readFailed, content.read_failed, ...bodies.flatMap(body => [body.readFailure, body.read_failure, body.readFailed, body.read_failed])]
  const queryOnlyFlags = [res && res.queryOnly, res && res.query_only, res && res.onlyQuery, res && res.only_query, content.queryOnly, content.query_only, content.onlyQuery, content.only_query, ...bodies.flatMap(body => [body.queryOnly, body.query_only, body.onlyQuery, body.only_query])]
  const stubFlags = [res && res.stub, content.stub, ...bodies.map(body => body.stub)]
  const mockFlags = [res && res.mock, res && res.mockMode, res && res.mock_mode, res && res.mockFallback, res && res.mock_fallback, content.mock, content.mockMode, content.mock_mode, content.mockFallback, content.mock_fallback, ...bodies.flatMap(body => [body.mock, body.mockMode, body.mock_mode, body.mockFallback, body.mock_fallback])]
  if (paymentUnconfirmedFlags.some(isActiveGuardFlag) || values.some(isPaymentStatusUnconfirmedToken)) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if (blockedFlags.some(isActiveGuardFlag) || values.some(isBlockedToken)) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if (writeFlags.some(isActiveGuardFlag)) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if (readFlags.some(isActiveGuardFlag) ||
    values.some(value => isUnavailableToken(value) && markerToken(value).includes('read'))) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if (unimplementedFlags.some(isActiveGuardFlag) ||
    values.some(value => isUnavailableToken(value) && (
      markerToken(value).includes('unimplemented') ||
      markerToken(value).includes('not_implemented') ||
      markerToken(value).includes('not implemented')
  ))) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if (unsupportedFlags.some(isActiveGuardFlag) ||
    values.some(value => isUnavailableToken(value) && (
      markerToken(value).includes('not_supported') ||
      markerToken(value).includes('not-supported') ||
      markerToken(value).includes('not supported') ||
      markerToken(value).includes('unsupported')
  ))) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if (notConnectedFlags.some(isActiveGuardFlag) ||
    values.some(value => isUnavailableToken(value) && (
      markerToken(value).includes('not_connected') ||
      markerToken(value).includes('not-connected') ||
      markerToken(value).includes('not connected') ||
      markerToken(value).includes('route_unavailable') ||
      markerToken(value).includes('route-unavailable') ||
      markerToken(value).includes('route unavailable') ||
      markerToken(value).includes('routeunavailable')
  ))) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if (readFailureFlags.some(isActiveGuardFlag) || [res, content, ...bodies].some(hasInvalidStrictProofMarker) ||
    values.some(value => isUnavailableToken(value) && (
      markerToken(value).includes('read_failure') ||
      markerToken(value).includes('read-failure') ||
      markerToken(value).includes('read failure') ||
      markerToken(value).includes('read_failed') ||
      markerToken(value).includes('read-failed') ||
      markerToken(value).includes('read failed')
  ))) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if (technicalFlags.some(isActiveGuardFlag) ||
    values.some(value => isUnavailableToken(value) && (
      markerToken(value).includes('technical_error') ||
      markerToken(value).includes('technical-error') ||
      markerToken(value).includes('technical error') ||
      markerToken(value).includes('technicalerror') ||
      markerToken(value).includes('technical_exception') ||
      markerToken(value).includes('technical-exception') ||
      markerToken(value).includes('technical exception') ||
      markerToken(value).includes('technicalexception') ||
      markerToken(value).includes('technical_marker') ||
      markerToken(value).includes('technical-marker') ||
      markerToken(value).includes('technical marker') ||
      markerToken(value).includes('technicalmarker') ||
      markerToken(value).includes('backend_failure') ||
      markerToken(value).includes('backend-failure') ||
      markerToken(value).includes('backend failure') ||
      markerToken(value).includes('backendfailure') ||
      markerToken(value).includes('技术错误') ||
      markerToken(value).includes('技术异常')
  ))) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if (fallbackFlags.some(isActiveGuardFlag) ||
    values.some(value => isUnavailableToken(value) && (
      markerToken(value).includes('fallback') ||
      markerToken(value).includes('placeholder') ||
      markerToken(value).includes('data_unavailable') ||
      markerToken(value).includes('data-unavailable') ||
      markerToken(value).includes('data unavailable')
  ))) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if (queryOnlyFlags.some(isActiveGuardFlag) ||
    values.some(value => isUnavailableToken(value) && (
      markerToken(value).includes('query_only') ||
      markerToken(value).includes('query-only') ||
      markerToken(value).includes('query only') ||
      markerToken(value).includes('only_query') ||
      markerToken(value).includes('only-query') ||
      markerToken(value).includes('only query') ||
      markerToken(value).includes('只支持查询') ||
      markerToken(value).includes('仅支持查询') ||
      markerToken(value).includes('只支持查詢') ||
      markerToken(value).includes('僅支持查詢')
  ))) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if (values.some(value => markerToken(value) === 'need_login')) return 'Please log in to continue'
  if (stubFlags.some(isActiveGuardFlag) ||
    values.some(value => markerToken(value) === 'stub')) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if (mockFlags.some(isActiveGuardFlag) ||
    values.some(value => markerToken(value).includes('mock'))) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  return failureFallbackMessage(fallback, 'This feature is temporarily unavailable. Please try again later.')
}

export function toArray(value) {
  return Array.isArray(value) ? value : []
}

export function toNumber(value, fallback = 0) {
  const num = Number(normalizeNumericValue(value))
  return Number.isFinite(num) ? num : fallback
}

function normalizeNumericValue(value) {
  if (typeof value === 'number') return value
  const raw = text(value)
  if (!raw) return raw
  const cleaned = raw.replace(/[^\d.,-]/g, '')
  if (!cleaned) return cleaned
  const lastComma = cleaned.lastIndexOf(',')
  const lastDot = cleaned.lastIndexOf('.')
  if (lastComma > -1 && lastDot > -1) {
    return lastComma > lastDot
      ? cleaned.replace(/\./g, '').replace(',', '.')
      : cleaned.replace(/,/g, '')
  }
  if (lastComma > -1) {
    const decimals = cleaned.length - lastComma - 1
    return decimals === 3 ? cleaned.replace(/,/g, '') : cleaned.replace(',', '.')
  }
  if ((cleaned.match(/\./g) || []).length > 1) {
    const lastSeparator = cleaned.lastIndexOf('.')
    return cleaned.slice(0, lastSeparator).replace(/\./g, '') + cleaned.slice(lastSeparator)
  }
  return cleaned
}

function text(value) {
  return String(value == null ? '' : value).trim()
}

function lower(value) {
  return text(value).toLowerCase()
}

function firstValue(source, keys) {
  const data = source && typeof source === 'object' ? source : {}
  for (const key of keys) {
    if (data[key] !== undefined && data[key] !== null && text(data[key])) {
      return data[key]
    }
  }
  return ''
}

function hasAnyField(source, keys) {
  const data = source && typeof source === 'object' ? source : {}
  return keys.some(key => data[key] !== undefined && data[key] !== null && text(data[key]))
}

function hasWeakOrderStatusMetadata(item = {}) {
  const content = contentOf(item)
  const bodies = responseBodies(item)
  const weakKeys = [
    'id',
    'requestId',
    'request_id',
    'traceId',
    'trace_id',
    'updated',
    'changed',
    'saved',
    'statusChanged',
    'status_changed',
    'updatedRows',
    'updated_rows',
    'affected_rows',
    'postBalance',
    'post_balance'
  ]
  return [item, content, ...bodies].some(source => hasAnyField(source, weakKeys))
}

function payOrderFallbackStatus(currentStatus) {
  return String(currentStatus) === '2' ? '2' : '0'
}

function normalizePayOrderToken(value) {
  const raw = lower(value)
  if (!raw) return ''
  if (raw === 'pay$success' || raw === 'withdraw$success') return '1'
  if (raw === 'pay$failed' || raw === 'withdraw$failed') return '2'
  if (['1', 'success', 'successful', 'succeed', 'succeeded', 'paid', 'complete', 'completed', 'approved', 'done', 'finish', 'finished'].includes(raw) || raw.includes('success') || raw.includes('paid') || raw.includes('complete')) {
    return '1'
  }
  if (isTerminalFailureToken(raw)) {
    return '2'
  }
  if (['0', 'pending', 'wait', 'waiting', 'processing', 'created', 'unpaid'].includes(raw) || raw.includes('pending') || raw.includes('waiting') || raw.includes('processing')) {
    return '0'
  }
  return ''
}

function payOrderStatusSources(payload) {
  const root = payload && typeof payload === 'object' ? payload : {}
  const sources = []
  const seen = []
  const append = (source, allowGenericStatus) => {
    if (!source || typeof source !== 'object' || seen.includes(source)) return
    seen.push(source)
    sources.push({ source, allowGenericStatus })
  }

  const content = contentOf(root)
  append(content, content !== root)
  responseBodies(root).forEach(body => append(body, true))
  firstArray(content, ['list', 'records', 'dataList']).forEach(item => append(item, true))
  firstArray(root, ['list', 'records', 'dataList']).forEach(item => append(item, true))
  append(root, hasAnyField(root, ['payStatus', 'paymentStatus', 'payment_status', 'orderStatus', 'order_status', 'statusCode', 'status_code', 'paid', 'paySuccess', 'paymentSuccess']))
  return sources
}

export function hasPayOrderStatusEvidence(payload = {}) {
  if (isNeedLogin(payload) || hasUnavailableMarker(payload)) return false
  if (hasPositivePaymentStatusProof(payload)) return true
  const statusEvidenceKeys = [
    'payStatus',
    'pay_status',
    'paymentStatus',
    'payment_status',
    'orderStatus',
    'order_status',
    'statusCode',
    'status_code',
    'payState',
    'pay_state',
    'paid',
    'paySuccess',
    'paymentSuccess'
  ]
  return payOrderStatusSources(payload).some(({ source }) => hasAnyField(source, statusEvidenceKeys))
}

export function normalizePayOrderStatus(payload = {}, currentStatus = '0') {
  if (isNeedLogin(payload) || hasUnavailableMarker(payload)) return payOrderFallbackStatus(currentStatus)
  const hasServerProof = hasPositivePaymentStatusProof(payload)
  const strongStatusKeys = [
    'payStatus',
    'paymentStatus',
    'payment_status',
    'orderStatus',
    'order_status',
    'statusCode',
    'status_code',
    'pay_state',
    'payState'
  ]
  const genericStatusKeys = ['status', 'state', 'code']

  for (const { source, allowGenericStatus } of payOrderStatusSources(payload)) {
    let status = normalizePayOrderToken(firstValue(source, strongStatusKeys))
    if (status) return status === '1' && !hasServerProof ? payOrderFallbackStatus(currentStatus) : status

    if (source.paid === true || markerToken(source.paid) === 'true') return hasServerProof ? '1' : payOrderFallbackStatus(currentStatus)
    if (source.paid === false || markerToken(source.paid) === 'false') return '2'
    if (source.paySuccess === true || source.paymentSuccess === true) return hasServerProof ? '1' : payOrderFallbackStatus(currentStatus)
    if (source.paySuccess === false || source.paymentSuccess === false || markerToken(source.paySuccess) === 'false' || markerToken(source.paymentSuccess) === 'false') return '2'

    if (allowGenericStatus || hasAnyField(source, strongStatusKeys)) {
      status = normalizePayOrderToken(firstValue(source, genericStatusKeys))
      if (status) return status === '1' && !hasServerProof ? payOrderFallbackStatus(currentStatus) : status
      if (source.success === false || markerToken(source.success) === 'false') return '2'
    }
  }
  return payOrderFallbackStatus(currentStatus)
}

export function normalizeOrderType(item = {}) {
  const rawType = lower(firstValue(item, ['orderType', 'order_type', 'type', 'dealType', 'deal_type', 'recordType', 'record_type', 'transactionType', 'transaction_type']))
  const rawStatus = lower(firstValue(item, ['orderStatus', 'order_status', 'withdrawStatus', 'withdraw_status', 'rechargeStatus', 'recharge_status', 'paymentStatus', 'payment_status', 'payStatus', 'pay_status', 'status', 'state', 'statusCode', 'status_code']))
  if (rawType === 'wd' || rawType === 'w' || rawType.includes('withdraw') || rawStatus.includes('withdraw')) return 'wd'
  if (rawType === 'pay' || rawType === 'p' || rawType === 'r' || rawType.includes('recharge') || rawType.includes('deposit') || rawStatus.includes('pay')) return 'pay'
  return rawType || rawStatus ? 'pay' : ''
}

export function normalizeOrderStatus(item = {}, orderType = normalizeOrderType(item)) {
  const prefix = orderType === 'wd' ? 'withdraw' : 'pay'
  if (hasUnavailableMarker(item)) return `${prefix}$failed`
  const explicitStatus = firstValue(item, ['orderStatus', 'order_status', 'withdrawStatus', 'withdraw_status', 'rechargeStatus', 'recharge_status', 'paymentStatus', 'payment_status', 'payStatus', 'pay_status', 'statusCode', 'status_code'])
  const raw = lower(explicitStatus || firstValue(item, ['status', 'state']))
  if (!raw) return ''
  const hasServerProof = hasPositivePaymentStatusProof(item)
  const needsServerProof = Boolean(explicitStatus) || hasWeakOrderStatusMetadata(item)
  if (raw === 'withdraw$success' || raw === 'pay$success') return !needsServerProof || hasServerProof ? raw : `${prefix}$pending`
  if (raw === 'withdraw$failed' || raw === 'pay$failed') return raw
  if (['1', 'success', 'succeed', 'succeeded', 'complete', 'completed', 'paid', 'approved', 'done', 'finish', 'finished'].includes(raw) || raw.includes('success') || raw.includes('paid') || raw.includes('complete')) {
    if (needsServerProof && !hasServerProof) return `${prefix}$pending`
    return `${prefix}$success`
  }
  if (isTerminalFailureToken(raw)) {
    return `${prefix}$failed`
  }
  return `${prefix}$pending`
}

export function isSuccessfulOrderStatus(status) {
  return ['withdraw$success', 'pay$success'].includes(status)
}

export function isFailedOrderStatus(status) {
  return ['withdraw$failed', 'pay$failed'].includes(status)
}

export function displayOrderStatusName(item = {}, lang = {}) {
  if (isSuccessfulOrderStatus(item.orderStatus)) {
    if (item.orderType === 'pay') return lang.recharge_success || 'Recharge Successful'
    return item.orderStatusName || lang.Sucesso || 'Success'
  }
  if (item.orderStatusName) return item.orderStatusName
  if (isFailedOrderStatus(item.orderStatus)) return lang.common_txt376 || 'Failed'
  if (item.orderStatus && item.orderStatus.indexOf('$pending') > -1) return lang.common_txt67 || 'Pending'
  return '-'
}

export function normalizeOrderRecord(item = {}, lang = {}) {
  const orderType = normalizeOrderType(item)
  const orderStatus = normalizeOrderStatus(item, orderType)
  const orderNo = firstValue(item, ['orderNo', 'order_no', 'orderId', 'order_id', 'recordNo', 'record_no', 'recordId', 'record_id', 'payOrderNo', 'pay_order_no', 'rechargeOrderNo', 'recharge_order_no', 'wdOrderNo', 'wd_order_no', 'withdrawOrderNo', 'withdraw_order_no', 'tradeNo', 'trade_no'])
  const orderAmount = firstValue(item, ['orderAmount', 'order_amount', 'transactionAmount', 'transaction_amount', 'money', 'amount', 'payAmount', 'pay_amount', 'withdrawAmount', 'withdraw_amount'])
  const rawOrderStatusName = hasUnavailableMarker(item) ? '' : text(firstValue(item, ['orderStatusName', 'statusName', 'status_name', 'stateName']))
  const orderStatusName = !isSuccessfulOrderStatus(orderStatus) && isOptimisticSuccessMessage(rawOrderStatusName) ? '' : rawOrderStatusName
  return {
    ...item,
    orderNo: text(orderNo),
    orderType,
    orderTypeName: text(firstValue(item, ['orderTypeName', 'typeName', 'dealTypeName'])) || (orderType ? (orderType === 'wd' ? (lang.Saque || 'Withdraw') : (lang.Recarregar || 'Recharge')) : ''),
    orderAmount: toNumber(orderAmount),
    orderStatus,
    orderStatusName: orderStatusName || displayOrderStatusName({ orderType, orderStatus }, lang),
    currency: text(firstValue(item, ['currency', 'currencyCode', 'currency_code'])),
    createDate: text(firstValue(item, ['createDate', 'create_date', 'createTime', 'create_time', 'createdAt', 'created_at', 'time'])),
    bankName: text(firstValue(item, ['bankName', 'bank_name', 'bank', 'bankTitle', 'bank_title'])),
    bankCard: text(firstValue(item, ['bankCard', 'bank_card', 'cardNo', 'card_no', 'accountNo', 'account_no', 'cardNumber', 'card_number'])),
    remark: text(firstValue(item, ['remark', 'memo', 'reason']))
  }
}

export function normalizeAddBankColumns(bankList) {
  return toArray(bankList).map(item => {
    const value = firstValue(item, ['bankId', 'bank_id', 'bankCode', 'bank_code', 'code', 'id', 'value'])
    const label = firstValue(item, ['bankName', 'bank_name', 'bankTitle', 'bank_title', 'title', 'shortName', 'short_name', 'label', 'name'])
    return {
      value: text(value),
      label: text(label)
    }
  }).filter(item => item.value && item.label)
}

function normalizePlatform(item) {
  const source = item && typeof item === 'object' ? item : {}
  return {
    iconUrl: text(firstValue(source, ['iconUrl', 'icon', 'flagUrl'])),
    areaCode: text(firstValue(source, ['areaCode', 'code'])),
    countryName: text(firstValue(source, ['countryName', 'name'])),
    country: text(firstValue(source, ['country', 'countryCode'])),
    platform: text(firstValue(source, ['platform'])),
    currencyUnit: text(firstValue(source, ['currencyUnit', 'currencySymbol'])),
    currency: text(firstValue(source, ['currency'])),
    defaultLanguage: text(firstValue(source, ['defaultLanguage', 'language']))
  }
}

export function normalizePlatformList(res) {
  return firstArray(contentOf(res), ['list', 'platformList', 'dataList', 'records'])
    .map(normalizePlatform)
    .filter(item => item.areaCode || item.country || item.platform)
}

export function getAddBankSubmitBlockReason({ addBankColumns, addBankId, addBankName } = {}) {
  if (!toArray(addBankColumns).length) return 'BANK_LIST_UNAVAILABLE'
  if (!text(addBankId) || !text(addBankName)) return 'BANK_REQUIRED'
  return ''
}

export function safeParseJson(value, fallback = {}) {
  if (!value || typeof value !== 'string') return fallback
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' ? parsed : fallback
  } catch (e) {
    return fallback
  }
}

export function firstArray(content, keys) {
  if (Array.isArray(content)) return content
  const source = content && typeof content === 'object' ? content : {}
  for (const key of keys) {
    if (Array.isArray(source[key])) return source[key]
  }
  for (const wrapper of RESPONSE_KEYS) {
    if (Array.isArray(source[wrapper])) return source[wrapper]
    if (source[wrapper] && typeof source[wrapper] === 'object') {
      const nested = firstArray(source[wrapper], keys)
      if (nested.length) return nested
    }
  }
  return []
}
